import type { AxisEvidenceScoreRecord, AxisLensReadRecord, EvidenceReceiptRecord } from '../lib/api';
import { humanizeAxisKey } from './AxisLensCard';
import styles from './JobFitRadar.module.css';
import { JOB_FIT_RADAR_SCAFFOLD_AXES, deriveJobFitRadarView } from './jobFitAxisPath';
import { axisPoints, gridPoints, ringPoints, toPolygonString } from './radarPath';

/** The three-state evidence trichotomy label the legend + aria description read
 *  straight off the derived per-axis value (FIX-E part 2, Lane L-H):
 *    null → "not scored yet" (scorer didn't run) · 0 → "no evidence yet" (ran,
 *    zero real receipts — the honest grounded zero) · > 0 → "N%" (receipt-backed).
 *  The null-vs-0 split is the no-invention line; never conflated in copy. */
function evidenceLabel(pct: number | null): string {
  if (pct === null) return 'not scored yet';
  if (pct === 0) return 'no evidence yet';
  return `${pct}%`;
}

/** A native-tooltip receipt trail for a scored axis — WHICH real achievements
 *  ground it (NO INVENTION made visible). Surfaced via the row's `title`
 *  attribute (a standard HTML affordance, not a new UI surface — the mission's
 *  "receipts on tap only if the idiom has a disclosure; do not invent new
 *  surfaces"): the legend row already exists, this only annotates it. Empty
 *  receipts → no title (the "no evidence yet" row needs none). */
function receiptTitle(receipts: EvidenceReceiptRecord[] | undefined): string | undefined {
  if (!receipts || receipts.length === 0) return undefined;
  const bodies = receipts.slice(0, 4).map((r) => r.matchedText).filter(Boolean);
  const more = receipts.length > 4 ? ` (+${receipts.length - 4} more)` : '';
  const noun = receipts.length === 1 ? 'achievement' : 'achievements';
  return `Grounded in ${receipts.length} of your ${noun}: ${bodies.join('; ')}${more}`;
}

const SIZE = 220;
const CENTER = SIZE / 2;
const RADIUS = 78;
const RING_SCALES = [0.25, 0.5, 0.75, 1];

/**
 * D24 (2026-07-08 live-test, his refined spec, supersedes D11's original
 * self-vs-self framing) — "what we need to see is how we feel a job is in
 * ONE layer, and how my skill set as described so far fits to match that
 * shape. It will not match entirely. And the spider-web labels don't fit
 * within the frame — use a colored dot marker and describe them below;
 * space is at a premium on mobile, and mobile will be ninety-five percent
 * of our use."
 *
 * LAYER 1 (var(--tl-accent), solid) — the JOB's own cornerstone shape:
 * AxisLensReadRecord.weights, the SAME served read AxisLensCard's composed
 * bar already renders, plotted as a radar instead of a bar. Always real.
 *
 * LAYER 2 (var(--tl-warm), dashed) — the candidate's evidence-so-far against
 * that SAME axis set. HONEST MISMATCH EXPECTED — his own words, "it will not
 * match entirely" — this is not a bug in the shape, it IS the shape's value.
 * At the time this ships no per-axis candidate-evidence signal is wired onto
 * GET /jobs/:id (see jobFitAxisPath.ts's header for the full grounded
 * finding); `evidenceByAxisKey` is therefore always empty in the current
 * caller and this layer renders its own honest "not scored against your
 * evidence yet" note instead of a fabricated flat/zero polygon — never
 * invents a value NO INVENTION would forbid. The component itself is
 * already wired for the day a real per-axis evidence source lands (an
 * additive prop, not a rewrite).
 *
 * H2 FIX-C (2026-07-10, docs/qa/FIX-PLAN-H2-axisread-starvation-2026-07-10.md
 * — the render pin, "render-sparse-never-remove"): the ring+spoke scaffold
 * below now draws in EVERY state (previously two early-returns collapsed
 * anything under 3 traced axes to a bare text line, hiding the chart's own
 * bones the moment a lake-ingested listing's requirements[] came back
 * empty). 3+ real axes keeps this exact, byte-identical render. 1-2 real
 * axes draws the same scaffold with those real points as highlighted stems +
 * dots — never a closed polygon, which would need an invented third vertex
 * to mean anything. 0 axes draws the scaffold alone (a fixed, honest
 * 6-spoke resting shape — see jobFitAxisPath.ts's JOB_FIT_RADAR_SCAFFOLD_AXES)
 * with the existing honest caption composed beneath it, never a lone text
 * line. All state/copy resolution moved to jobFitAxisPath.ts's
 * deriveJobFitRadarView (pure, independently testable — same convention this
 * file's own LAYER 2 honesty logic already follows). FIX-C2 additionally
 * surfaces a quiet provenance caption when the served axisRead.coverageNote
 * is 'unstructured_read' (Lane 1's JD-source seam: the ask was traced from
 * the posting's free-text description, never extracted requirement bullets)
 * — api.ts already types coverageNote as bare `string | null`, so this new
 * server-side value needed no shared-type edit.
 *
 * MOBILE-FIRST LABELS ("95% of our use," his ruling) — NO in-chart SVG text
 * labels (RadarChart.tsx's/LensComparisonRadar.tsx's own text-on-the-spoke
 * labels are exactly what he flagged as overflowing the frame). Each vertex
 * is a plain colored DOT marker instead; the axis names + values live in a
 * legend list BELOW the chart, matching LensComparisonRadar's own
 * legend-below convention (that component already proves the pattern for
 * this app) — extended here to carry the per-axis label AND both layers'
 * values on one row, since D24 needs both numbers, not just a swatch/name
 * pair.
 */
export function JobFitRadar({
  axisRead,
  evidenceByAxisKey,
  evidenceScores,
}: {
  axisRead: AxisLensReadRecord | null;
  /** Per-axisKey candidate evidence, 0..1. Optional/additive — the legacy
   *  ship-time prop (superseded by evidenceScores when that is present). */
  evidenceByAxisKey?: Record<string, number> | null;
  /** FIX-E part 2 (Lane L-H) — the per-axis GROUNDED evidence read (getJob's
   *  userScores). Additive: when present it drives the three-state trichotomy
   *  (not scored / no evidence / N%) + the Layer-2 gold polygon + the receipt
   *  tooltips; when absent the component renders exactly as it did before. */
  evidenceScores?: AxisEvidenceScoreRecord[] | null;
}) {
  const jobFitView = deriveJobFitRadarView(
    axisRead,
    evidenceByAxisKey,
    humanizeAxisKey,
    evidenceScores,
  );
  // The receipt trail per axis (NO INVENTION made visible), for the legend row
  // tooltips — keyed by axisKey, empty when userScores wasn't served.
  const receiptsByAxis = new Map(
    (evidenceScores ?? []).map((s) => [s.axisKey, s.receipts] as const),
  );

  // H2 FIX-C — "render-sparse-never-remove": the ring+spoke scaffold below
  // draws in EVERY state (no_read/empty/sparse/full). Only the closed data
  // polygon is gated on >= 3 real axes; 0/1/2 real axes still draw the same
  // scaffold plus whatever is honestly real (nothing, or bare stems+dots) —
  // the chart's own bones never collapse to a bare text line.
  if (jobFitView.kind === 'no_read' || jobFitView.kind === 'empty') {
    const scaffoldGrid = gridPoints(JOB_FIT_RADAR_SCAFFOLD_AXES, CENTER, RADIUS);
    return (
      <div className={styles.wrap}>
        {/* Purely decorative at 0 axes — nothing here carries information
            beyond "empty scaffold shape," so the SVG is aria-hidden and the
            VISIBLE caption below is the one accessible description (avoids a
            screen reader announcing the same message twice). */}
        <svg viewBox={`0 0 ${SIZE} ${SIZE}`} width="100%" aria-hidden="true">
          {scaffoldGrid.map((p, i) => (
            <line
              // biome-ignore lint/suspicious/noArrayIndexKey: scaffoldGrid is a fixed-count, purely positional geometry array (never reorders/filters) — no other stable identity exists for "the i-th neutral scaffold vertex."
              key={`scaffold-spoke-${i}`}
              x1={CENTER}
              y1={CENTER}
              x2={p.x}
              y2={p.y}
              className={styles.spoke}
            />
          ))}
          {RING_SCALES.map((scale) => (
            <polygon
              key={`scaffold-ring-${scale}`}
              points={toPolygonString(
                ringPoints(JOB_FIT_RADAR_SCAFFOLD_AXES, CENTER, RADIUS, scale),
              )}
              className={styles.ring}
            />
          ))}
        </svg>
        <div className={styles.scaffoldCaption}>{jobFitView.caption}</div>
      </div>
    );
  }

  const { points, hasCompleteEvidence, provenanceCaption } = jobFitView;
  const isSparse = jobFitView.kind === 'sparse';

  const jobValues = points.map((p) => p.jobWeightPct);
  const jobPoints = axisPoints(jobValues, CENTER, RADIUS);
  // Sparse (1-2 real axes) keeps the FIXED neutral scaffold count — the same
  // resting silhouette as the empty state above. A 1- or 2-spoke grid would
  // itself be a degenerate, near-invisible shape (radarPath.ts's
  // ringPoints/gridPoints need >= 3 vertices to draw a visible polygon).
  // Full (3+) keeps today's real-count-driven grid, byte-identical to before
  // this fix.
  const scaffoldAxisCount = isSparse ? JOB_FIT_RADAR_SCAFFOLD_AXES : points.length;
  const outerGrid = gridPoints(scaffoldAxisCount, CENTER, RADIUS);

  // Layer 2 only plots where a real (non-null) evidence value exists for
  // EVERY axis — a partial evidence set would draw a polygon that silently
  // treats "not yet scored" as "scored at zero," which is exactly the
  // invented-absence class NO INVENTION forbids. Full-honesty rule: the
  // evidence polygon draws only when every plotted axis has a real value;
  // otherwise the legend still shows per-axis "pending" rows (never hidden),
  // just no fabricated connecting shape.
  const evidenceValues = hasCompleteEvidence ? points.map((p) => p.evidencePct as number) : null;
  const evidencePoints = evidenceValues ? axisPoints(evidenceValues, CENTER, RADIUS) : null;

  return (
    <div className={styles.wrap}>
      <svg
        viewBox={`0 0 ${SIZE} ${SIZE}`}
        width="100%"
        role="img"
        aria-label={`How this role's cornerstones compare to your evidence so far: ${points
          .map((p) => `${p.label} — role wants ${p.jobWeightPct}%, your evidence ${evidenceLabel(p.evidencePct)}`)
          .join('; ')}`}
      >
        {outerGrid.map((p, i) => (
          <line
            // biome-ignore lint/suspicious/noArrayIndexKey: outerGrid is a fixed-count, purely positional geometry array (never reorders/filters) — no other stable identity exists for "the i-th grid vertex."
            key={`spoke-${i}`}
            x1={CENTER}
            y1={CENTER}
            x2={p.x}
            y2={p.y}
            className={styles.spoke}
          />
        ))}

        {RING_SCALES.map((scale) => (
          <polygon
            key={`ring-${scale}`}
            points={toPolygonString(ringPoints(scaffoldAxisCount, CENTER, RADIUS, scale))}
            className={styles.ring}
          />
        ))}

        {isSparse ? (
          <>
            {/* render-sparse-never-remove: 1-2 real axes draw as highlighted
                stems + dots on their own spokes — never a closed polygon,
                which would need a fabricated third vertex to mean anything. */}
            {jobPoints.map((p, i) => (
              <line
                key={`job-stem-${points[i].axisKey}`}
                x1={CENTER}
                y1={CENTER}
                x2={p.x}
                y2={p.y}
                className={styles.jobStem}
              />
            ))}
            {jobPoints.map((p, i) => (
              <circle
                key={`job-dot-${points[i].axisKey}`}
                cx={p.x}
                cy={p.y}
                r={3.4}
                className={styles.jobDot}
              />
            ))}
            {evidencePoints ? (
              <>
                {evidencePoints.map((p, i) => (
                  <line
                    key={`evidence-stem-${points[i].axisKey}`}
                    x1={CENTER}
                    y1={CENTER}
                    x2={p.x}
                    y2={p.y}
                    className={styles.evidenceStem}
                  />
                ))}
                {evidencePoints.map((p, i) => (
                  <circle
                    key={`evidence-dot-${points[i].axisKey}`}
                    cx={p.x}
                    cy={p.y}
                    r={3.4}
                    className={styles.evidenceDot}
                  />
                ))}
              </>
            ) : null}
          </>
        ) : (
          <>
            {/* Layer 1 — the job's own cornerstone shape. Always real, always
                drawn. */}
            <polygon points={toPolygonString(jobPoints)} className={styles.jobFill} />
            <polygon points={toPolygonString(jobPoints)} className={styles.jobStroke} />
            {jobPoints.map((p, i) => (
              <circle
                key={`job-dot-${points[i].axisKey}`}
                cx={p.x}
                cy={p.y}
                r={3.4}
                className={styles.jobDot}
              />
            ))}

            {/* Layer 2 — candidate evidence, only when every axis has a real
                value (see hasCompleteEvidence above). Dashed stroke visually
                distinguishes "your evidence" from the job's solid cornerstone
                shape, mirroring LensComparisonRadar's own before/after dash
                convention. */}
            {evidencePoints ? (
              <>
                <polygon points={toPolygonString(evidencePoints)} className={styles.evidenceFill} />
                <polygon
                  points={toPolygonString(evidencePoints)}
                  className={styles.evidenceStroke}
                />
                {evidencePoints.map((p, i) => (
                  <circle
                    key={`evidence-dot-${points[i].axisKey}`}
                    cx={p.x}
                    cy={p.y}
                    r={3.4}
                    className={styles.evidenceDot}
                  />
                ))}
              </>
            ) : null}
          </>
        )}
      </svg>

      {/* MOBILE-FIRST LEGEND — dot marker + label + both layers' values, one
          row per axis, BELOW the chart (never in-chart text labels, per his
          ruling). Order matches the chart's own spoke order (points), so a
          reader can trace a legend row to its spoke position going clockwise
          from 12 o'clock. Renders for BOTH sparse and full — sparse honestly
          shows just its 1-2 rows, never padded to look fuller. */}
      <div className={styles.legendHeader}>
        <span className={styles.legendHeaderItem}>
          <span className={`${styles.dotSwatch} ${styles.dotSwatchJob}`} aria-hidden="true" />
          What this role wants
        </span>
        <span className={styles.legendHeaderItem}>
          <span className={`${styles.dotSwatch} ${styles.dotSwatchEvidence}`} aria-hidden="true" />
          Your evidence so far
        </span>
      </div>
      <div className={styles.legend}>
        {points.map((p) => (
          <div key={p.axisKey} className={styles.legendRow} title={receiptTitle(receiptsByAxis.get(p.axisKey))}>
            <span className={styles.legendLabel}>{p.label}</span>
            <span className={styles.legendValues}>
              <span className={styles.legendValueJob}>{p.jobWeightPct}%</span>
              <span className={styles.legendValueEvidence}>{evidenceLabel(p.evidencePct)}</span>
            </span>
          </div>
        ))}
      </div>
      {!hasCompleteEvidence ? (
        <div className={styles.pendingNote}>
          Your evidence isn't scored against these specific cornerstones yet — the role's own shape
          (left column above) is real; your side is honestly marked pending rather than guessed.
        </div>
      ) : null}
      {/* H2 FIX-C2 — the quiet source-provenance caption for coverageNote
          'unstructured_read' (Lane 1's JD-source seam). */}
      {provenanceCaption ? <div className={styles.provenanceNote}>{provenanceCaption}</div> : null}
    </div>
  );
}
